import { useEffect, useState } from "react";
import { X, Download, Share, PlusSquare } from "lucide-react";

const DISMISSED_KEY = "ssh_pwa_prompt_dismissed_at";
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * PWA install prompt — handles Android/Chrome AND iOS/Safari.
 *
 * Android/Chrome fires `beforeinstallprompt`; we capture it and let the user
 * tap "Install app" which triggers the native prompt.
 *
 * iOS Safari does NOT fire that event — no JS-triggered install exists on
 * iOS. So we detect iOS Safari (and the site not already running as a PWA)
 * and instead render a small illustrated guide showing the user to tap
 * Share → "Add to Home Screen". Same 7-day dismiss cooldown applies.
 */
function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // Chrome/Firefox on iOS still use WebKit — they can't install PWAs either
  // (only Safari can), so we treat all iOS browsers the same.
  const isStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches
                     || window.navigator.standalone === true;
  return isIOS && !isStandalone;
}

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState("android"); // "android" | "ios"

  useEffect(() => {
    const last = parseInt(localStorage.getItem(DISMISSED_KEY) || "0", 10);
    const cooldownActive = Date.now() - last < COOLDOWN_MS;

    // iOS path — no event fires, so we show the manual instructions banner
    // after a short delay (so it doesn't cover the fold on first paint).
    if (isIosSafari()) {
      setPlatform("ios");
      if (!cooldownActive) {
        const t = setTimeout(() => setVisible(true), 3500);
        return () => clearTimeout(t);
      }
      return;
    }

    // Android / desktop Chrome path
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!cooldownActive) setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installed = () => {
      setVisible(false); setDeferred(null);
    };
    window.addEventListener("appinstalled", installed);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* user cancelled */ }
    setVisible(false); setDeferred(null);
  };

  if (!visible) return null;
  if (platform === "android" && !deferred) return null;

  const isIOS = platform === "ios";

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-80 z-40 bg-[#0B1120] border border-brand-blue/60 shadow-2xl backdrop-blur"
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] brand-orange mb-1">
              {isIOS ? "// Add to Home Screen" : "// Get the app"}
            </div>
            <div className="font-display text-lg font-black uppercase leading-tight">
              Install <span className="brand-blue">HTP Sports Hub</span>
            </div>
            <p className="text-xs text-white/60 mt-1.5">
              {isIOS
                ? "iPhone / iPad users — tap the Share icon in Safari, then \"Add to Home Screen\"."
                : "Book slots, mark attendance and receive session reminders — offline, fullscreen, one tap from your home screen."}
            </p>
          </div>
          <button
            onClick={dismiss}
            data-testid="pwa-dismiss"
            aria-label="Dismiss"
            className="text-white/50 hover:text-white shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {isIOS ? (
          // iOS: illustrated 2-step guide (no auto-install possible)
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-3 bg-white/5 px-3 py-2">
              <div className="w-6 h-6 flex items-center justify-center bg-brand-blue text-white font-bold text-xs">1</div>
              <div className="flex items-center gap-2 text-xs text-white/80">
                <Share size={14} className="brand-blue"/>
                Tap the <b>Share</b> icon
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/5 px-3 py-2">
              <div className="w-6 h-6 flex items-center justify-center bg-brand-orange text-black font-bold text-xs">2</div>
              <div className="flex items-center gap-2 text-xs text-white/80">
                <PlusSquare size={14} className="brand-orange"/>
                Pick <b>Add to Home Screen</b>
              </div>
            </div>
            <button
              onClick={dismiss}
              data-testid="pwa-ios-got-it"
              className="w-full mt-2 border border-white/15 hover:bg-white/10 py-2 text-[11px] font-mono uppercase tracking-widest text-white/70"
            >
              Got it
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 mt-4">
            <button
              onClick={install}
              data-testid="pwa-install"
              className="flex-1 bg-brand-blue text-white py-2.5 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 hover:glow-blue"
            >
              <Download size={13} /> Install
            </button>
            <button
              onClick={dismiss}
              className="px-3 py-2.5 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white border border-white/15"
            >
              Later
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
